import LBox from "@components/LBox";
import React, { ReactNode, useState } from "react";
import { LBoxProps } from "@theme";
import LPressable from "@components/LPressable";
import Header, { HeaderProps } from "@components/Accordion/Header";
import Content, { ContentProps } from "@components/Accordion/Content";
import { AccordionContext } from "./Context";

type AccordionProps = LBoxProps & {
  children: ReactNode;
  disabled?: boolean;
  initialOpen?: boolean;
  setOpenState?: (isOpen: boolean) => void;
  showDivider?: boolean;
  title: string;
};

const Accordion: React.FC<AccordionProps> & {
  Header: React.FC<HeaderProps>;
  Content: React.FC<ContentProps>;
} = ({
  children,
  disabled,
  initialOpen = false,
  setOpenState,
  showDivider = true,
  title,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const toggleAccordion = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    setOpenState?.(newIsOpen);
  };
  const header = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === Header,
  );
  const content = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === Content,
  );

  return (
    <AccordionContext.Provider value={{ isOpen, toggleAccordion }}>
      <LBox {...rest}>
        <LPressable
          disabled={disabled}
          onPress={toggleAccordion}
          sentryEventName={`accordion_${title}`}
        >
          {header}
        </LPressable>
        {isOpen && content}
        {showDivider && (
          <LBox height={1} backgroundColor="textGrey" width="100%" />
        )}
      </LBox>
    </AccordionContext.Provider>
  );
};

Accordion.Header = Header;
Accordion.Content = Content;

export default Accordion;
